import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import service from "../services/productNotificationMappingsService";
import type { ProductNotificationMappingView, ProductNotificationSelections, ProductNotificationTarget } from "../types/productNotificationMappings";
import { describeUserNotificationError } from "../components/notifications/notificationErrors";
import type { HttpError } from "../services/httpService";
import { hasNotificationChannels, selectionsFromMapping } from "../utils/productNotificationSelections";
import { consumeShareSignInTradeAlerts, hasShareSignInTradeAlertsState, withoutShareSignInTradeAlertsState } from "../utils/shareSignInTradeAlerts";

type MappingState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "unavailable" }
  | { status: "ready"; value: ProductNotificationMappingView; revision: number };

function isUnavailable(error: unknown): boolean {
  const description = describeUserNotificationError(error);
  const body: unknown = (error as HttpError | null)?.response?.data;
  const code = body && typeof body === "object" && "code" in body ? body.code : description.code;
  // USER_NOT_FOUND is also a 404; it is an account error, not an absent mapping.
  return description.status === 404 && code === "PRODUCT_UNAVAILABLE";
}

/** Mount under an actor/product key. Only confirmed server selections drive the switch. */
export function useProductNotificationMapping(productId: number, productType: ProductNotificationTarget["productType"] = "PORTFOLIO") {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const navigation = useRef({ location, navigate, navigationType, userId: user?.id });
  navigation.current = { location, navigate, navigationType, userId: user?.id };
  const autoEnable = useRef<{ userId: string | undefined; pathname: string; productType: ProductNotificationTarget["productType"]; productId: number } | null>(null);
  const [autoSubscriptionMessage, setAutoSubscriptionMessage] = useState<string | null>(null);
  const [state, setState] = useState<MappingState>({ status: "loading" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const readRequest = useRef(0);
  const readAbort = useRef<AbortController | null>(null);
  const writeRequest = useRef(0);
  const busy = useRef(false);

  const invalidateRead = useCallback(() => {
    ++readRequest.current;
    readAbort.current?.abort();
  }, []);

  const load = useCallback(async () => {
    invalidateRead();
    const token = readRequest.current;
    const controller = new AbortController();
    readAbort.current = controller;
    const current = () => token === readRequest.current && !controller.signal.aborted
      && navigation.current.userId === user?.id && navigation.current.location.pathname === location.pathname;
    const shouldAutoEnable = autoEnable.current?.userId === user?.id && autoEnable.current?.pathname === location.pathname
      && autoEnable.current?.productType === productType && autoEnable.current?.productId === productId;
    const request = async (operation: () => Promise<ProductNotificationMappingView>) => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await operation();
        } catch (error) {
          if (!shouldAutoEnable || !current() || attempt === 2) throw error;
          await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)));
          if (!current()) throw error;
        }
      }
    };
    setState({ status: "loading" });
    setSaveError(null);
    setAutoSubscriptionMessage(null);
    let autoSaving = false;
    try {
      let value = await request(() => service.get(productId, controller.signal, productType));
      if (!current()) return;
      autoEnable.current = null;
      if (shouldAutoEnable && value.product.productId === productId && value.eligibility.eligible
        && !hasNotificationChannels(selectionsFromMapping(value.mapping))) {
        autoSaving = true;
        busy.current = true;
        setSaving(true);
        value = await request(() => service.upsert(productId, {
          shouldEmail: true, shouldWebInbox: true, shouldMobileInbox: true, shouldPush: true,
        }, productType));
        if (!current()) return;
        if (value.product.productId === productId && value.eligibility.eligible && hasNotificationChannels(selectionsFromMapping(value.mapping))) {
          setAutoSubscriptionMessage(`You're now subscribed to trade alerts for ${value.product.title || `${productType === "BASKET" ? "Basket" : "Portfolio"} #${productId}`}.`);
        }
      }
      // The API allows PUBLIC and SUBSCRIBER portfolios without a subscription. Investment,
      // composition visibility and strategy membership do not determine this.
      setState(value.eligibility.eligible ? { status: "ready", value, revision: token } : { status: "unavailable" });
    } catch (error) {
      if (!current()) return;
      autoEnable.current = null;
      setState(shouldAutoEnable || isUnavailable(error) ? { status: "unavailable" }
        : { status: "error", message: describeUserNotificationError(error).message });
    } finally {
      if (autoSaving && current()) { busy.current = false; setSaving(false); }
    }
  }, [invalidateRead, productId, productType, user?.id, location.pathname]);

  useEffect(() => {
    const entry = navigation.current;
    busy.current = false;
    setSaving(false);
    if (consumeShareSignInTradeAlerts(entry.location.state, entry.userId, entry.location.pathname, productType, productId, entry.navigationType)) {
      autoEnable.current = { userId: entry.userId, pathname: entry.location.pathname, productType, productId };
    }
    if (hasShareSignInTradeAlertsState(entry.location.state)) {
      entry.navigate(`${entry.location.pathname}${entry.location.search}${entry.location.hash}`, {
        replace: true,
        state: withoutShareSignInTradeAlertsState(entry.location.state),
      });
    }
    void load();
    return () => { invalidateRead(); ++writeRequest.current; };
  }, [load, invalidateRead, productId, productType]);

  const reload = () => {
    if (busy.current) return;
    setNotice(null);
    void load();
  };

  const save = async (selections: ProductNotificationSelections): Promise<boolean> => {
    if (busy.current || state.status !== "ready") return false;
    busy.current = true;
    setSaving(true);
    setSaveError(null);
    setNotice(null);
    setAutoSubscriptionMessage(null);
    // A read issued before this write must not restore old channel selections.
    invalidateRead();
    const token = ++writeRequest.current;
    const current = () => token === writeRequest.current;
    try {
      const value = await service.upsert(productId, selections, productType);
      if (!current()) return false;
      setState(value.eligibility.eligible ? { status: "ready", value, revision: readRequest.current } : { status: "unavailable" });
      return value.eligibility.eligible;
    } catch (error) {
      if (!current()) return false;
      const detail = describeUserNotificationError(error);
      if (isUnavailable(error)) {
        setState({ status: "unavailable" });
      } else if (detail.ambiguous || (detail.status ?? 0) >= 500) {
        // A timeout/server failure can follow a committed write. Clear the
        // draft and re-read before another edit instead of claiming rollback.
        setNotice("Could not confirm the save. Review the refreshed selections before making another change.");
        await load();
      } else if ([401, 403, 404].includes(detail.status ?? 0)) {
        setState({ status: "error", message: detail.message });
      } else {
        setSaveError(detail.message);
      }
      return false;
    } finally {
      if (current()) { busy.current = false; setSaving(false); }
    }
  };

  return { state, saving, saveError, notice, reload, save, autoSubscriptionMessage };
}

export type ProductNotificationMappingController = ReturnType<typeof useProductNotificationMapping>;
